// Market-specific tuning values, extracted from balance.ts to
// respect the §10 400-line cap. Phase 4 Step 18.
//
// Composed back into `balance` via balance.ts as:
//   npc: balanceMarket.npc, economy: balanceMarket.economy
//
// Assignment (not spread) preserves `as const` literal types through
// the composition — TS can lose the `readonly` modifier across
// `...spread` depending on structure. This file is the canonical
// home for any future market/economy tuning; new subsystems get
// their own sibling balance-*.ts per the DECISIONS entry for Step 18.

export const balanceMarket = {
  npc: {
    // Pricing curve shape ratio — k = maxInv * priceCurveShapeRatio,
    // price = basePrice * (maxInv + k) / (currentInv + k). Ratio-based
    // so the swing (price_at_qty=0 / price_at_qty=maxInv) is uniform
    // across catalog items regardless of maxInv. At ratio=0.2 the
    // swing is uniform 6×. See DECISIONS 2026-04-18 "Phase 4 Step 8
    // NPC pricing curve uses maxInv-relative ratio". Tunable; rerun
    // `npm run npc:curve` after changes.
    priceCurveShapeRatio: 0.2,

    // NPC's spread relative to the curve price.
    //   sellPrice = price * (1 + baseMarginRatio)   (NPC sells to player)
    //   buyPrice  = price * (1 - baseMarginRatio)   (NPC buys from player)
    baseMarginRatio: 0.2,

    // Time between NPC restock alarms. Each fire adds
    // restockQuantityPerItem to every catalog item (clamped at maxInv).
    restockIntervalMs: 15 * 60 * 1000,

    // Units added to each NPC item's inventory on every restock alarm.
    // Cap is maxInv per item (no overshoot).
    restockQuantityPerItem: 10,
  },

  economy: {
    // Starting credits granted to every player on first DO instantiation.
    // Applied as the SQL DEFAULT on the `player.credits` column (see
    // Phase 4 Step 3 migration). Tune for early-game pacing.
    startingCredits: 100,

    // Hard ceiling on a single player's credit balance. addCredits
    // returns err("credits_overflow") rather than crossing this. Set
    // to half of MAX_SAFE_INTEGER — astronomically above legitimate
    // use, but cheap insurance against runaway-loop bugs crossing
    // the float-precision boundary. Math.floor keeps it an integer
    // so it round-trips through SQLite INTEGER and Number.isInteger.
    maxCredits: Math.floor(Number.MAX_SAFE_INTEGER / 2),

    // Listing price bounds.
    // minListingPrice: smallest valid listing price. With
    // listingFeeRatio=0.01, a price=1 listing pays a fee of 1 (ceil)
    // and the seller nets 0.
    minListingPrice: 1,
    // maxListingPriceMultiplier: cap on listing price relative to
    // the NPC's floor sell price (NPC-tradable items only). Crafted
    // items have no reference and skip the cap. See DECISIONS
    // 2026-04-18 "Phase 4 Step 9 max-price multiplier applies to
    // NPC-tradable items only".
    maxListingPriceMultiplier: 100,
    // listingFeeRatio: fraction of price retained on list (non-
    // refundable on cancel/expire). Ceil()'d, minimum 1c.
    listingFeeRatio: 0.01,
    // listingMaxDurationMs: TTL after which an unsold listing
    // expires and refunds to the seller (fee retained). 7 days.
    listingMaxDurationMs: 7 * 24 * 60 * 60 * 1000,
    // reservingTtlMs: a 'reserving' listing past this age is treated
    // as abandoned; the next buyListing resets to 'active' and
    // proceeds. PlayerDO idempotency log guards against double-debit.
    // See DECISIONS 2026-04-18 "Phase 4 Step 9 reserving-TTL".
    reservingTtlMs: 30 * 1000,
    // expirationSweepIntervalMs: alarm cadence for the expiration
    // sweep. Daily is fine (listings live at most 1 day past TTL).
    // Tests use runExpirationSweepNow() for deterministic asserts.
    expirationSweepIntervalMs: 24 * 60 * 60 * 1000,
    // reconciliationIntervalMs: seller-payment reconciliation alarm
    // cadence (Step 10). Infinite retry — see DECISIONS 2026-04-18
    // "Phase 4 Step 10 infinite retry".
    reconciliationIntervalMs: 5 * 60 * 1000,
    // reconciliationWarningInterval: every Nth failed retry emits
    // one warning. With a 5-min cadence, 10 = ~50-min stuck markers.
    reconciliationWarningInterval: 10,
    // Per-request row cap on MarketDO.getPriceHistory; sized for the sparkline UI.
    priceHistoryDefaultLimit: 200,
    // History-tab range picker options (Phase 4 Step 18). Fixed
    // buckets that map to `sinceTs = now - rangeMs` on the getPriceHistory
    // request. Order preserved for left-to-right render order.
    priceHistoryRanges: [
      { label: "1h", ms: 60 * 60 * 1000 },
      { label: "6h", ms: 6 * 60 * 60 * 1000 },
      { label: "24h", ms: 24 * 60 * 60 * 1000 },
      { label: "7d", ms: 7 * 24 * 60 * 60 * 1000 },
    ],
  },
} as const;
